import { useApiClient } from "@/src/hooks/useApiClient";

/**
 * Les photos de la publication signalée, quand il y en a.
 *
 * ## Pourquoi ce hook existe
 *
 * `reportedContent` est une **recopie du texte** au moment du signalement. Une
 * galerie peut n'en avoir aucun : ce sont alors les **photos** qui sont
 * signalées, et la carte n'affichait qu'un « contenu non conservé » trompeur —
 * la pièce à juger existe, elle n'est simplement pas dans `reportedContent`. On
 * va la chercher par `targetPostId`.
 *
 * ## La route d'administration, et plus la route publique
 *
 * ⚠️ **`GET /post/{id}` ne convient pas, et l'angle mort était connu** :
 * `canViewPost` n'y fait aucune exception de rôle, donc une publication retirée
 * ou refusée y répond 403 même à un administrateur — c'est-à-dire précisément
 * les dossiers où l'on a déjà agi et où l'on revient vérifier ce qu'on a jugé.
 * `GET /moderation/post/{id}`, livrée le 2 septembre, voit les quatre statuts et
 * les publications que leur auteur a retirées.
 *
 * L'échec reste **silencieux** — ni toast, ni message : une publication effacée
 * définitivement répond 404, et la citation conservée par le serveur est le
 * repli prévu. Une requête en échec ne doit pas ressembler à un dossier abîmé.
 *
 * ⚠️ **Ne fais pas transiter `moderationScores` par la carte d'un signalement.**
 * La réponse porte désormais les scores bruts du classifieur ; ils n'ont rien à
 * faire ici — ce hook ne rend que les photos, et c'est ce qui l'en garde.
 */
export function useReportedPostPhotos(postId: number | null) {
  const $api = useApiClient();

  const { data } = $api.useQuery(
    "get",
    "/api/v1/moderation/post/{id}",
    // ⚠️ `postId ?? 0` n'est jamais envoyé : `enabled` coupe la requête avant.
    // Le chemin doit malgré tout être typé, un hook ne pouvant pas être appelé
    // conditionnellement.
    { params: { path: { id: postId ?? 0 } } },
    {
      enabled: postId !== null,
      // Un 404 est une réponse, pas un incident : la publication a été effacée
      // définitivement, et le réessayer trois fois ne ferait que retarder le
      // repli.
      retry: false,
    },
  );

  return data?.data?.photos ?? [];
}
